import React, { useEffect, memo, useState } from 'react';
import ModalWithPattern from './ModalWithPattern';
import SvgQuestion from '../../../atoms/icons/SvgQuestion';
import styled from '@emotion/styled';
import { isDarkMode, IsMobileViewPort } from '../../../utils/helpers';
import { getPatternModal } from '../../../style';
import PrimaryButton from '../../../atoms/buttons/PrimaryButton';
import { useDisableBodyElementScroll } from '../../../hooks/useDisableBodyScroll';
import StepOne from '../../../atoms/icons/StepOne';
import breakpoints from 'theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';

const FirstDateOfferContainer = styled('div')({
  display: 'initial',
});

const FirstDateOfferDescContainer = styled('div')({
  marginBottom: 28,
  [breakpoints.down(639.05)]: {
    paddingLeft: 30,
    paddingRight: 30,
  },
});

const StyledSpan = styled('span')({
  fontSize: 13,
  lineHeight: '16px',
  fontWeight: 400,
  cursor: 'pointer',
  color: '#8392A7',
  '&:hover': {
    textDecoration: 'underline',
    color: '#2490DE',
  },
});

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = {
  borderRadius: 10,
  maxHeight: 'auto',
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
};

const FirstDateModalBodyContainer = styled('div')({
  textAlign: 'center',
  marginTop: 20,
});

const TitleContainer = styled('h3')(() => ({
  fontStyle: 'normal',
  fontWeight: 700,
  color: getPatternModal().titleColor,
  fontSize: IsMobileViewPort ? 22 : 22,
  lineHeight: '35px',
  letterSpacing: '-0.44px',
  marginTop: 20,
  marginBottom: 20,
}));

const ContentList = styled('ul')({
  listStyleType: 'disc',
  lineHeight: '24px',
  minHeight: 100,
  paddingLeft: 20,
});

const ContentListItem = styled('li')({
  textAlign: 'left',
  paddingBottom: 10,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '23px',
  fontFamily: FONT_FAMILIES.primary,
});

interface FirstDateOfferModalProps {
  customStyle?: React.CSSProperties;
  iconOnly?: boolean;
  linkStyle?: Record<string, unknown>;
  showTutorial?: boolean;
  onClose?(): void;
  svgIconHeight?: number;
  svgIconWeight?: number;
  defaultMinOffer: string;
  currencyPrefix: string;
}

const FirstDateOfferModal = ({
  iconOnly = false,
  linkStyle = {},
  showTutorial = false,
  svgIconWeight = 12,
  svgIconHeight = 12,
  customStyle = {},
  defaultMinOffer,
  currencyPrefix,
}: FirstDateOfferModalProps): JSX.Element => {
  const [showOffersTutorial, setShowOffersTutorial] = useState(false);

  useDisableBodyElementScroll(showOffersTutorial);

  useEffect(() => {
    setShowOffersTutorial(showTutorial);

    () => {
      setShowOffersTutorial(false);
    };
  }, [showTutorial]);

  const handleOnLinkClick = e => {
    e.preventDefault();

    if (!showOffersTutorial) {
      setShowOffersTutorial(true);
    }
  };

  const handleCloseModal = () => {
    setShowOffersTutorial(false);
  };

  return (
    <FirstDateOfferContainer style={customStyle}>
      <StyledSpan style={linkStyle} onClick={e => handleOnLinkClick(e)}>
        <SvgQuestion
          viewBox="0 0 512 500"
          styles={{
            fill: isDarkMode() ? '#71B5E4' : '#2490DE',
            height: svgIconHeight,
            width: svgIconWeight,
          }}
        />{' '}
        {iconOnly ? '' : 'What’s this?'}
      </StyledSpan>
      <ModalWithPattern
        dataTestID="first-date-offer-tutorial-modal"
        isModalOpen={showOffersTutorial}
        modalContentLabel="First Date Offer Tutorial"
        closeOnOverlayClick
        closeRequest={handleCloseModal}
        closeBtnClassName="first-date-offer-tutorial-close"
        closeBtnDataTestId="first-date-offer-tutorial-close"
        onCloseClick={handleCloseModal}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={IsMobileViewPort ? mobileContentStyle : { maxWidth: 500 }}
      >
        <FirstDateModalBodyContainer>
          <StepOne
            customStyle={{
              width: IsMobileViewPort ? 90 : 93,
              height: IsMobileViewPort ? 90 : 87,
            }}
          />

          <TitleContainer>{`What is a First Date Offer?`}</TitleContainer>
          <FirstDateOfferDescContainer>
            <ContentList>
              <ContentListItem>
                It’s is an incentive given to you by a Generous member; a gift intended to encourage you to go on a
                first date.
              </ContentListItem>
              <ContentListItem>
                {`Your opening offer amount can be anywhere from ${currencyPrefix}${defaultMinOffer} to ${currencyPrefix}500 and is displayed on your profile`}
              </ContentListItem>
              <ContentListItem>
                Any offers you get that are less than this amount can be countered by you, giving you the final say.
              </ContentListItem>
              <ContentListItem style={{ fontWeight: 700 }}>
                It’s hard work to get ready for a date, you should get paid for it!
              </ContentListItem>
            </ContentList>
          </FirstDateOfferDescContainer>

          <PrimaryButton
            color="red"
            size="small"
            data-test-id="close-first-date-offer-modal"
            onClick={handleCloseModal}
            label="Got It"
            customStyle={{
              marginBottom: 10,
              ...(IsMobileViewPort ? { height: 40, fontSize: 14, marginBottom: 15 } : {}),
            }}
          />
        </FirstDateModalBodyContainer>
      </ModalWithPattern>
    </FirstDateOfferContainer>
  );
};

export default memo(FirstDateOfferModal);
